import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import styles from '../../../styles/pages/Transactions/components/Withdrawal.module.css';
import { getPaymentsApi } from "../../../utils/api/Transaction/getPaymentsApi";
import { EditPaymentModal } from '../../../components/Modal/Payments/EditPaymentsModal';
import { Notification } from '../../../components/UI/Notifications';
import { LoadingCircle } from '../../../components/UI/Custom/LoadingCircle';
import { LangContext } from "../../../context/LangContext";
import { ThemeContext } from "../../../context/ThemeContext";

import DoneIcon from '@mui/icons-material/Done';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CancelIcon from '@mui/icons-material/Cancel';
import BlockIcon from '@mui/icons-material/Block';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';

const translations = {
    ru: {
        perPage: "Показывать по:",
        previous: "Пред.",
        next: "След.",
        page: (currentPage, totalPages) => `Страница ${currentPage} из ${totalPages}`,
        updateSuccess: "Платеж успешно обновлён!",
        refreshToSeeChanges: "Чтобы увидеть изменения — обновите страницу",
    },
    en: {
        perPage: "Show per page:",
        previous: "Prev.",
        next: "Next",
        page: (currentPage, totalPages) => `Page ${currentPage} of ${totalPages}`,
        updateSuccess: "Payment updated successfully!",
        refreshToSeeChanges: "Refresh the page to see the changes",
    },
};

export const Withdrawal = () => {
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const t = translations[langSelected] || translations.ru;
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedPaymentId, setSelectedPaymentId] = useState(null);
    const [notification, setNotification] = useState(null);
    const [currentPage, setPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [totalPages, setTotal] = useState(1);
    const [highlightedStatuses, setHighlightedStatuses] = useState([]);

    useEffect(() => {
        const storedSettings = localStorage.getItem('highlightedStatuses');
        if (storedSettings) {
            try {
                setHighlightedStatuses(JSON.parse(storedSettings));
            } catch (error) {
                console.error("Failed to parse settings from localStorage", error);
                setHighlightedStatuses([]);
            }
        }
    }, []);

    useEffect(() => {
        setLoading(true);
        getPaymentsApi({ action: 'withdraw', page: currentPage, perPage })
            .then(({ data, meta }) => {
                setItems(data);
                setTotal(meta.lastPage);
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [currentPage, perPage]);

    const openEditModal = (paymentId) => {
        setSelectedPaymentId(paymentId);
        setIsEditModalOpen(true);
    };

    const prev = () => setPage(p => Math.max(1, p - 1));
    const next = () => setPage(p => Math.min(totalPages, p + 1));

    const getStatusClassName = (status) => {
        switch (status) {
            case 'success': return styles.statusSuccess;
            case 'process': return styles.statusProcess;
            case 'fail': return styles.statusFail;
            case 'canceled': return styles.statusCanceled;
            case 'waitaccept': return styles.statusWaitAccept;
            default: return '';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'success': return DoneIcon;
            case 'process': return HourglassEmptyIcon;
            case 'fail': return CancelIcon;
            case 'canceled': return BlockIcon;
            case 'waitaccept': return HourglassTopIcon;
            default: return null;
        }
    };

    const getHighlightClassName = (status) => {
        let baseClass = '';
        if (highlightedStatuses.includes(status)) {
            switch (status) {
                case 'success': baseClass = styles.highlightSuccess; break;
                case 'process': baseClass = styles.highlightProcess; break;
                case 'fail': baseClass = styles.highlightFail; break;
                case 'canceled': baseClass = styles.highlightCanceled; break;
                case 'waitaccept': baseClass = styles.highlightWaitAccept; break;
                default: baseClass = '';
            }
        }
        return `${baseClass} ${theme === 'dark' ? styles.darkTableRow : ''}`;
    };

    return (
        <div className={theme === 'dark' ? styles.darkWrapper : ''}>
            {loading ? (
                <div className={styles.loadingWrapper}>
                    <LoadingCircle size={30} thickness={1} />
                </div>
            ) : (
                <>
                    <div className={styles.paginationControlsTop}>
                        <div className={`${styles.perPageSelector} ${theme === 'dark' ? styles.darkText : ''}`}>
                            <label>{t.perPage}</label>
                            <select
                                className={theme === 'dark' ? styles.darkSelect : ''}
                                value={perPage}
                                onChange={e => {
                                    setPerPage(Number(e.target.value));
                                    setPage(1);
                                }}
                            >
                                {[10, 20, 50, 100].map(n => (
                                    <option key={n} value={n}>{n}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <table className={`${styles.table} ${theme === 'dark' ? styles.darkTable : ''}`}>
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Lead ID</th>
                                <th>Assets</th>
                                <th>Amount</th>
                                <th>Status</th>
                                <th>Date</th>
                                <th>Created By</th>
                                <th>Bank</th>
                                <th>Wallet</th>
                                <th>№ Card</th>
                                <th>Comment</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(tx => (
                                <tr key={tx.id} className={getHighlightClassName(tx.status)}>
                                    <td
                                        className={styles.tableCell}
                                        onClick={() => openEditModal(tx.id)}
                                        style={{ cursor: 'pointer', color: theme === 'dark' ? '#8a7de4' : '#007bff', fontWeight: 'bold' }}
                                    >
                                        {tx.id}
                                    </td>
                                    <td className={styles.tableCell}>
                                        <Link to={`/lead/change/info/${tx.lead_id}`} className={`${styles.link} ${theme === 'dark' ? styles.darkLink : ''}`}>
                                            {tx.lead_id}
                                        </Link>
                                    </td>
                                    <td className={styles.tableCell}>{tx.assets}</td>
                                    <td className={styles.tableCell}>{tx.amount}</td>
                                    <td className={styles.tableCell}>
                                        <div className={`${getStatusClassName(tx.status)} ${theme === 'dark' ? styles.darkStatusBox : ''}`}>
                                            {getStatusIcon(tx.status) && (
                                                React.createElement(getStatusIcon(tx.status), { style: { fontSize: '14px' } })
                                            )}
                                            {tx.status}
                                        </div>
                                    </td>
                                    <td className={styles.tableCell}>{tx.date}</td>
                                    <td className={styles.tableCell}>
                                        {tx.created_by_admin?.first_name} {tx.created_by_admin?.last_name}
                                    </td>
                                    <td className={styles.tableCell}>{tx.bank || '–'}</td>
                                    <td className={styles.tableCell}>{tx.wallet || '–'}</td>
                                    <td className={styles.tableCell}>{tx.number || '–'}</td>
                                    <td className={styles.tableCell}>{tx.comment || '–'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div className={`${styles.paginationControlsBottom} ${theme === 'dark' ? styles.darkPagination : ''}`}>
                        <button onClick={prev} disabled={currentPage === 1}>{t.previous}</button>
                        <span>{t.page(currentPage, totalPages)}</span>
                        <button onClick={next} disabled={currentPage === totalPages}>{t.next}</button>
                    </div>
                </>
            )}

            {isEditModalOpen && selectedPaymentId && (
                <EditPaymentModal
                    paymentId={selectedPaymentId}
                    onClose={() => setIsEditModalOpen(false)}
                    onPaymentActionSuccess={() => {
                        setIsEditModalOpen(false);
                        setPage(1);
                        setNotification({
                            message: (<>{t.updateSuccess}<br />{t.refreshToSeeChanges}</>),
                            type: 'success'
                        });
                    }}
                />
            )}

            {notification && (
                <Notification
                    message={notification.message}
                    type={notification.type}
                    onClose={() => setNotification(null)}
                />
            )}
        </div>
    );
};
